import React from 'react';
import { useNavigate } from 'react-router-dom';
import useSEO from '../hooks/useSEO';
import { trackEvent } from '../utils/analytics';
import { 
  OrganizationStructuredData, 
  MedicalBusinessStructuredData, 
  WebSiteStructuredData
} from '../components/StructuredData';
import { 
  ArrowRight, 
  CheckCircle, 
  Users, 
  TrendingUp, 
  DollarSign, 
  Clock, 
  Brain, 
  Shield, 
  Zap, 
  Heart, 
  BarChart3, 
  Target, 
  UserPlus, 
  MessageCircle, 
  Star, 
  Globe, 
  Lightbulb, 
  Award,
  Phone,
  Mail,
  Bot,
  Eye,
  FileText,
  Building,
  BadgeCheck,
  Calendar,
  Lock
} from 'lucide-react';
import PublicNavbar from '../components/PublicNavbar';
import Icon from '../components/Icon';
import Card from '../ui/Card';

const TherapistLandingPage = () => {
  const navigate = useNavigate();

  // SEO optimization
  useSEO({
    title: 'För Terapeuter - Your Therapist | AI-förstärkt terapiplattform',
    description: 'Anslut dig till Your Therapist som legitimerad terapeut. AI-förstärkt terapiplattform som hjälper dig nå fler klienter och förbättra behandlingsresultat.',
    keywords: 'terapeuter, AI-terapi, terapiplattform, legitimerad terapeut, psykolog, mental hälsa yrkesverksamma, terapeut registrering',
    image: 'https://www.yourtherapist.se/logo512.png',
    url: 'https://www.yourtherapist.se/therapist-landing',
    type: 'website'
  });

  const handleJoinNow = () => {
    // Track the click event
    trackEvent('Therapist Join Button Click', { location: 'therapist-landing-page' });
    navigate('/therapist-register');
  };

  const handleContactUs = () => {
    // Track the click event
    trackEvent('Contact Link Click', { location: 'therapist-landing-page' });
    navigate('/contact');
  };

  return (
    <div className="min-h-screen bg-primary dark:bg-gray-900 font-sans">
      <style jsx>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap');
        
        .font-inter {
          font-family: 'Inter', sans-serif;
        }
        
        .animate-gradient {
          background: linear-gradient(-45deg, var(--brand-accent), var(--brand-primary), var(--error-500), var(--warning-500));
          background-size: 400% 400%;
          animation: gradientShift 8s ease infinite;
        }
        
        @keyframes gradientShift {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        
        html {
          scroll-behavior: smooth;
        }
      `}</style>

      {/* Navigation */}
      <PublicNavbar 
        onContact={handleContactUs}
      />

      {/* Hero Section */}
      <section className="pt-20 sm:pt-24 md:pb-12 w-full bg-gradient-to-br from-neutral-50 via-bg-primary to-neutral-100 dark:from-gray-800 dark:via-gray-900 dark:to-gray-800 flex items-center justify-center">
        <div className="inset-0 bg-gradient-to-br from-brand-primary/5 via-brand-secondary/5 to-brand-accent/5"></div>
        <div className="pt-8 sm:pt-12 md:pt-16 text-center text-primary dark:text-white max-w-6xl mx-auto px-custom-4 sm:px-custom-6 md:px-custom-8 relative z-10">
          <div className="flex justify-center mb-8">
            <div className="w-20 h-20 bg-brand-primary rounded-full flex items-center justify-center">
              <Heart className="w-10 h-10 text-white" />
            </div>
          </div>
          
          <h1 className="text-4xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold mb-6 sm:mb-8 md:mb-10 text-brand-primary dark:text-white leading-tight">
            Framtidens terapi börjar här
          </h1>
          
          {/* Enhanced description */}
          <div className="max-w-3xl lg:max-w-4xl mx-auto mb-8 sm:mb-10 md:mb-12">
            <p className="text-lg sm:text-xl md:text-2xl mb-8 sm:mb-10 md:mb-12 text-secondary dark:text-gray-300 leading-relaxed text-center">
              Anslut dig till Your Therapist och få tillgång till nya klienter, AI-stöd för dina befintliga klienter och moderna verktyg som revolutionerar terapiarbetet. Ingen förmedlingsavgift - bara mer effektiv terapi.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-custom-4 sm:gap-custom-6 justify-center mb-8 sm:mb-10 md:mb-12">
            <button 
              onClick={handleJoinNow} 
              className="w-full sm:w-auto px-custom-6 sm:px-custom-8 md:px-custom-10 py-custom-3 sm:py-custom-4 md:py-custom-5 bg-brand-primary text-inverse rounded-lg text-base sm:text-lg md:text-xl font-bold hover:bg-brand-primary-dark transform hover:-translate-y-1 hover:shadow-custom-xl transition-custom duration-300 flex items-center justify-center gap-custom-3 shadow-custom-xl"
            >
              <UserPlus className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6" />
              <span>Anslut dig idag</span>
              <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6" />
            </button>
            <button 
              onClick={handleContactUs}
              className="w-full sm:w-auto px-custom-6 sm:px-custom-8 md:px-custom-10 py-custom-3 sm:py-custom-4 md:py-custom-5 bg-transparent border-2 border-brand-primary text-brand-primary rounded-lg text-base sm:text-lg md:text-xl font-semibold hover:bg-brand-primary/5 transition-custom duration-300 flex items-center justify-center gap-custom-3"
            >
              <MessageCircle className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6" />
              <span>Boka demo</span>
            </button>
          </div>
          
          {/* Meet our team link */}
          <div className="text-center">
            <button 
              onClick={() => {
                trackEvent('Meet Team Button Click', { location: 'therapist-landing-page' });
                navigate('/therapists');
              }}
              className="text-brand-primary hover:text-brand-primary-dark transition-colors text-lg font-medium underline decoration-2 underline-offset-4"
            >
              Möt vårt team av terapeuter
            </button>
          </div>
        </div>
      </section>

        {/* Benefits Section */}
        <section id="benefits" className="py-24 w-full bg-gradient-to-br from-gray-50 to-pink-50 dark:from-gray-800 dark:to-gray-900">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-primary dark:text-white mb-6">
                Varför Your Therapist?
              </h2>
              <p className="text-xl text-secondary dark:text-gray-300 max-w-3xl mx-auto">
                Vi ger dig verktygen och möjligheterna att växa din praktik och hjälpa fler människor
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              <Card
                icon={Users}
                iconBgColor="bg-brand-secondary"
                iconTextColor="text-white"
                title="Nya klienter utan förmedlingsavgifter"
                description="Få tillgång till människor som annars kanske aldrig hade sökt terapi. Vårt AI-stöd gör att tveksamma personer vågar ta första steget - och kan sedan övergå till dig."
              />

              <Card
                icon={Brain}
                iconBgColor="bg-brand-primary"
                iconTextColor="text-white"
                title="AI-stöd mellan sessionerna"
                description="Erbjud dina befintliga klienter 24/7 stöd mellan era möten. AI:n kommer ihåg era samtal och ger kontinuerligt stöd baserat på din behandlingsplan."
              />

              <Card
                icon={Lightbulb}
                iconBgColor="bg-brand-accent"
                iconTextColor="text-secondary"
                title="Moderna terapiverktyg"
                description="Automatisk journalföring, progressuppföljning och evidensbaserade övningar. Fokusera på terapi - vi hanterar administrationen."
              />

              <Card
                icon={Clock}
                iconBgColor="bg-brand-tertiary"
                iconTextColor="text-white"
                title="Total flexibilitet"
                description="Arbeta när du vill, från var du vill. Digital plattform som fungerar perfekt för både videomöten och textbaserad terapi."
              />

              <Card
                icon={TrendingUp}
                iconBgColor="bg-brand-secondary"
                iconTextColor="text-white"
                title="Högre intäkter per klient"
                description="Med AI-stöd kan du hantera fler klienter effektivt och erbjuda kontinuerlig support - vilket motiverar högre arvoden."
              />

              <Card
                icon={Shield}
                iconBgColor="bg-brand-primary"
                iconTextColor="text-white"
                title="Automatisk krissupport"
                description="AI:n identifierar krissignaler och eskalerar omedelbart till dig. Ingen klient faller mellan stolarna - även nattetid."
              />
            </div>
          </div>
        </section>

        {/* How it Works Section */}
        <section id="how-it-works" className="py-12 w-full bg-gradient-to-br from-surface-primary to-surface-secondary dark:from-gray-900 dark:to-gray-800">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-primary dark:text-white mb-6">
                Så enkelt kommer du igång
              </h2>
            </div>
            
            {/* Four step process with visual timeline */}
            <div className="relative">
              {/* Timeline line - hidden on mobile */}
              <div className="hidden lg:block absolute top-1/2 left-0 right-0 h-1 bg-gradient-to-r from-brand-primary via-brand-primary to-brand-primary rounded-full transform -translate-y-1/2 z-0"></div>
              
              <div className="grid grid-cols-1 lg:grid-cols-4 gap-12 relative z-10">
                {/* Step 1 */}
                <div className="bg-white dark:bg-gray-800 p-8 rounded-2xl shadow-lg border border-brand-primary/20 dark:border-gray-600 relative">
                  <div className="absolute -top-4 left-8 w-8 h-8 bg-brand-primary rounded-full flex items-center justify-center text-white font-bold text-sm lg:left-1/2 lg:transform lg:-translate-x-1/2">
                    1
                  </div>
                  <div className="mt-4">
                    <h3 className="text-xl font-bold text-primary dark:text-white mb-4">Ansök och verifieras</h3>
                    <p className="text-secondary dark:text-gray-300 leading-relaxed">
                      Ansök med dina legitimationsuppgifter. Vi verifierar din bakgrund hos Socialstyrelsen och onboardar dig säkert.
                    </p>
                  </div>
                </div>

                {/* Step 2 */}
                <div className="bg-white dark:bg-gray-800 p-8 rounded-2xl shadow-lg border border-brand-primary/20 dark:border-gray-600 relative">
                  <div className="absolute -top-4 left-8 w-8 h-8 bg-brand-primary rounded-full flex items-center justify-center text-white font-bold text-sm lg:left-1/2 lg:transform lg:-translate-x-1/2">
                    2
                  </div>
                  <div className="mt-4">
                    <h3 className="text-xl font-bold text-primary dark:text-white mb-4">Skapa din profil</h3>
                    <p className="text-secondary dark:text-gray-300 leading-relaxed">
                      Bygg din professionella profil med specialiseringar, behandlingsmetoder och tillgänglighet.
                    </p>
                  </div>
                </div>

                {/* Step 3 */}
                <div className="bg-white dark:bg-gray-800 p-8 rounded-2xl shadow-lg border border-brand-primary/20 dark:border-gray-600 relative">
                  <div className="absolute -top-4 left-8 w-8 h-8 bg-brand-primary rounded-full flex items-center justify-center text-white font-bold text-sm lg:left-1/2 lg:transform lg:-translate-x-1/2">
                    3
                  </div>
                  <div className="mt-4">
                    <h3 className="text-xl font-bold text-primary dark:text-white mb-4">Matchas med klienter</h3>
                    <p className="text-secondary dark:text-gray-300 leading-relaxed">
                      Vårt AI-system matchar dig med klienter baserat på behov, specialisering och personlighet.
                    </p>
                  </div>
                </div>

                {/* Step 4 */}
                <div className="bg-white dark:bg-gray-800 p-8 rounded-2xl shadow-lg border border-brand-primary/20 dark:border-gray-600 relative">
                  <div className="absolute -top-4 left-8 w-8 h-8 bg-brand-primary rounded-full flex items-center justify-center text-white font-bold text-sm lg:left-1/2 lg:transform lg:-translate-x-1/2">
                    4
                  </div>
                  <div className="mt-4">
                    <h3 className="text-xl font-bold text-primary dark:text-white mb-4">Börja behandla</h3>
                    <p className="text-secondary dark:text-gray-300 leading-relaxed">
                      Starta terapisessioner med fullständig bakgrundsinformation från AI-samtalen redan klar.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="text-center mt-12">
              <button
                onClick={handleJoinNow}
                className="px-8 py-4 bg-brand-primary text-white rounded-xl text-lg font-semibold hover:bg-brand-primary-dark transform hover:-translate-y-1 hover:shadow-2xl transition-all duration-300 flex items-center justify-center gap-3 mx-auto"
              >
                <UserPlus className="w-5 h-5" />
                Anslut dig idag
              </button>
            </div>
          </div>
        </section>

        {/* Features for Therapists */}
        <section id="features" className="py-12 w-full bg-gradient-to-br from-surface-primary to-surface-secondary dark:from-gray-800 dark:to-gray-900">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-primary dark:text-white mb-6">
                Funktioner för terapeuter
              </h2>
              <p className="text-xl text-secondary dark:text-gray-300 max-w-3xl mx-auto">
                Verktyg och funktioner designade för att förbättra din kliniska praxis och patientrelationer
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              <Card 
                icon={Brain}
                iconBgColor="bg-brand-primary"
                title="AI-genererad anamnestik"
                description="Få fullständig bakgrundsinformation och anamnestik innan första sessionen. Spara tid och förbättra behandlingsresultat."
              />

              <Card 
                icon={Users}
                iconBgColor="bg-brand-secondary"
                title="Smart matchning"
                description="Matchas med klienter baserat på specialisering, personlighet och behandlingsmetoder för optimala resultat."
              />

              <Card 
                icon={Calendar}
                iconBgColor="bg-brand-accent"
                iconTextColor="text-secondary"
                title="Flexibel schemaläggning"
                description="Hantera din tillgänglighet och boka sessioner enligt dina preferenser. Full kontroll över din arbetssituation."
              />

              <Card 
                icon={Lock}
                iconBgColor="bg-brand-tertiary"
                title="Säker kommunikation"
                description="Banksäker kryptering och GDPR-kompatibel hantering av all patientinformation och kommunikation."
              />

              <Card 
                icon={TrendingUp}
                iconBgColor="bg-brand-primary"
                title="Progressuppföljning"
                description="Följ patienternas framsteg med AI-assisterade verktyg och rapporter som förbättrar behandlingen."
              />

              <Card 
                icon={FileText}
                iconBgColor="bg-brand-secondary"
                title="Digital journalföring"
                description="Integrerad journalföring med AI-stöd för anteckningar och behandlingsplaner. Allt på en plats."
              />
            </div>
          </div>
        </section>

        {/* Pricing Section */}
        <section id="pricing" className="py-12 w-full bg-white dark:bg-gray-900">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-primary dark:text-white mb-6">
                Transparent prissättning
              </h2>
              <p className="text-xl text-secondary dark:text-gray-300 max-w-3xl mx-auto">
                Inga dolda avgifter. Du behåller mer av dina intäkter än traditionella plattformar.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-4xl mx-auto">
              {/* Monthly Fee Card */}
              <div className="p-8 bg-brand-primary/5 dark:bg-gray-800 border border-brand-primary/20 dark:border-gray-600 hover:border-brand-primary/40 dark:hover:border-gray-500 transition-all duration-300 hover:shadow-lg rounded-2xl">
                <div className="text-center">
                  <h3 className="text-2xl font-bold text-primary dark:text-white mb-4">Månadsavgift</h3>
                  <div className="text-4xl font-bold text-brand-primary mb-4">149 kr</div>
                  <p className="text-secondary dark:text-gray-300 leading-relaxed mb-6">
                    Fast månadsavgift för tillgång till plattformen och alla dess funktioner.
                  </p>
                  <ul className="text-left text-secondary dark:text-gray-300 space-y-2">
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-brand-primary flex-shrink-0" />
                      Tillgång till alla funktioner
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-brand-primary flex-shrink-0" />
                      AI-genererad anamnes
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-brand-primary flex-shrink-0" />
                      Digital journalföring
                    </li>
                  </ul>
                  <div className="text-center mt-12">
                    <button
                      onClick={handleJoinNow}
                      className="px-8 py-4 bg-brand-primary text-white rounded-xl text-lg font-semibold hover:bg-brand-primary-dark transform hover:-translate-y-1 hover:shadow-2xl transition-all duration-300 flex items-center justify-center gap-3 mx-auto"
                    >
                      Kom igång idag
                    </button>
                  </div>
                </div>
              </div>

              {/* Commission Fee Card */}
              <div className="p-8 bg-brand-secondary/5 dark:bg-gray-800 border border-brand-secondary/20 dark:border-gray-600 hover:border-brand-secondary/40 dark:hover:border-gray-500 transition-all duration-300 hover:shadow-lg rounded-2xl">
                <div className="text-center">
                  <h3 className="text-2xl font-bold text-primary dark:text-white mb-4">Förmedlingsavgift</h3>
                  <div className="text-4xl font-bold text-brand-secondary mb-4">1200 kr</div>
                  <p className="text-secondary dark:text-gray-300 leading-relaxed mb-6">
                    Engångsavgift per ny klient som matchas genom plattformen.
                  </p>
                  <ul className="text-left text-secondary dark:text-gray-300 space-y-2">
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-brand-secondary flex-shrink-0" />
                      Smart patientmatchning
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-brand-secondary flex-shrink-0" />
                      Förkvalificerade klienter
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-brand-secondary flex-shrink-0" />
                      Fullständig anamnes
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-brand-secondary flex-shrink-0" />
                      Säker videosamtal
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-brand-secondary flex-shrink-0" />
                      24/7 teknisk support
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            
          </div>
        </section>        {/* Testimonials */}
        

        {/* CTA Section */}
        <section className="py-24 w-full bg-brand-primary text-center text-white">
          <div className="max-w-5xl mx-auto px-6">
            <div className="mb-8 flex justify-center">
              <div className="w-20 h-20 bg-white/20 backdrop-blur-sm border border-white/30 rounded-full flex items-center justify-center">
                <UserPlus className="w-10 h-10 text-white" />
              </div>
            </div>
            <h2 className="text-4xl md:text-6xl font-bold mb-6">
              Redo att transformera din praktik?
            </h2>
            <p className="text-xl mb-12 text-white/90 max-w-3xl mx-auto leading-relaxed">
              Anslut dig till revolutionen inom mental hälsovård. Hjälp fler människor och väx din praktik med AI-stöd.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-6 justify-center mb-16">
              <button 
                onClick={handleJoinNow} 
                className="px-12 py-6 bg-white text-red-600 rounded-xl text-xl font-bold hover:bg-gray-100 hover:-translate-y-1 hover:shadow-2xl transition-all duration-300 flex items-center justify-center gap-3 shadow-xl"
              >
                Anslut dig nu
                <ArrowRight className="w-6 h-6" />
              </button>
              <button 
                onClick={handleContactUs}
                className="px-12 py-6 bg-transparent border-2 border-white text-white rounded-xl text-xl font-semibold hover:bg-white/10 transition-all duration-300 flex items-center justify-center gap-3"
              >
                <MessageCircle className="w-6 h-6" />
                Boka demo
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto text-center">
              <div className="bg-white/10 backdrop-blur-sm p-6 rounded-2xl border border-white/20">
                <div className="text-3xl font-bold mb-2">Gratis</div>
                <div className="text-white/80">Att komma igång</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm p-6 rounded-2xl border border-white/20">
                <div className="text-3xl font-bold mb-2">24/7</div>
                <div className="text-white/80">Support för dig & klienter</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm p-6 rounded-2xl border border-white/20">
                <div className="text-3xl font-bold mb-2">100%</div>
                <div className="text-white/80">GDPR-säkert</div>
              </div>
            </div>
          </div>
        </section>

        {/* Structured Data for SEO */}
        <OrganizationStructuredData />
    </div>
  );
};

export default TherapistLandingPage;
